import { useEffect, useState } from "react";
import { login, register } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { PlanetBackground } from "@/components/PlanetBackground";

interface LoginPageProps {
  onLogin: (token: string) => void;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const { setTheme } = useTheme();

  // Login screen is dark-theme only (owner decision) — no theme switcher.
  useEffect(() => {
    setTheme("dark");
  }, [setTheme]);
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await login(email, password);
        onLogin(res.access_token);
      } else {
        // register returns tokens and stores them (v3.1)
        const res = await register(email, password, name);
        onLogin(res.access_token);
      }
    } catch (err: any) {
      setError(err?.message || "Ошибка входа");
    } finally {
      setBusy(false);
    }
  };

  // Auth layout: planet-field scene behind the existing form. The form
  // itself (fields, brand, validation, logic) is unchanged; only the page
  // layer changed — dark #060B12 background + decorative canvas. min-h-dvh
  // + overflow-x-hidden: the long register form scrolls vertically, no
  // horizontal scroll, no clipping on keyboard focus.
  return (
    <div className="auth-page relative flex min-h-dvh items-center justify-center overflow-x-hidden text-primary font-sans">
      <PlanetBackground />
      <div className="relative z-10 w-full max-w-sm px-6 py-10">
        <div className="flex flex-col items-center gap-4 mb-10">
          <h1 className="text-4xl md:text-5xl font-serif tracking-tight flex items-center gap-2.5 select-none">
            <span className="text-accent glow-cyan">Reality</span>
            <span className="text-white glow-white">AI</span>
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {mode === "register" && (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Имя"
              className="w-full bg-elevated border border-strongline rounded-xl px-4 py-3 text-primary placeholder:text-tertiary focus:outline-none focus:border-accent/50 transition-colors"
            />
          )}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Электронная почта"
            required
            className="w-full bg-elevated border border-strongline rounded-xl px-4 py-3 text-primary placeholder:text-tertiary focus:outline-none focus:border-accent/50 transition-colors"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Пароль"
            required
            className="w-full bg-elevated border border-strongline rounded-xl px-4 py-3 text-primary placeholder:text-tertiary focus:outline-none focus:border-accent/50 transition-colors"
          />

          {error && <div className="text-sm text-red-400 px-1">{error}</div>}

          <button
            type="submit"
            disabled={busy || !email || !password}
            className="w-full py-3 rounded-xl bg-accent text-accent-contrast font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {busy ? "Подождите..." : mode === "login" ? "Войти" : "Зарегистрироваться"}
          </button>

          <button
            type="button"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-sm text-secondary hover:text-primary transition-colors py-1"
          >
            {mode === "login" ? "Нет аккаунта? Зарегистрируйтесь" : "Уже есть аккаунт? Войдите"}
          </button>
        </form>
      </div>
    </div>
  );
}